import React, { useState, useEffect } from 'react';
import { Form, Select, Button, Table, Empty, Skeleton, Divider, message, Space, Typography, Row, Col, Card } from 'antd';
import { HomeOutlined, UnorderedListOutlined, UserOutlined, FileSearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import PageApp from '../../../Page';
import { obtenerHistoricosPorEmpleado } from '../../../../apis/HistoricoNominaApi'
import { getAll } from '../../../../apis/PeriodosApi';
import { getEmpleados } from '../../../../apis/OdooApi';
import Currency from '../../../../components/common/Currency';
import { status_period } from '../../../../constants/period';
import {status_recibo_nomina} from '../../../../constants/reciboNomina';

const { Option } = Select;
const { Text } = Typography;

const routes = [
  {
    href: '/',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '/historico-nominas',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Histórico de Nóminas</span>
      </>
    ),
  }
];

const HistoricoNominaSimple = () => {
  const [form] = Form.useForm();
  const [empleados, setEmpleados] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [historicData, setHistoricData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empleadoInfo, setEmpleadoInfo] = useState(null);

  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      try {
        const [empleadosResponse, periodosResponse] = await Promise.all([
          getEmpleados(),
          getAll()
        ]);
        
        if (empleadosResponse.ok) {
          setEmpleados(empleadosResponse.payload || []);
        } else {
          message.error('Error al cargar la lista de empleados');
        }
        
        if (periodosResponse.ok) {
          setPeriodos(periodosResponse.payload || []);
        } else {
          message.error('Error al cargar la lista de periodos');
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        message.error('Error al cargar datos iniciales');
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatos();
  }, []);

  const getPeriodoText = (periodoId) => {
    const periodo = periodos.find(p => p.id === periodoId);
    console.log('Periodo encontrado:', periodo);
    return periodo ? periodo.tipo : 'Desconocido';
  };

  const obtenerHistorico = async () => {
    const empleadoId = form.getFieldValue('empleado_id');
    
    if (!empleadoId) {
      message.warning('Por favor seleccione un empleado');
      return;
    }
    
    setLoading(true);
    try {
      const response = await obtenerHistoricosPorEmpleado(empleadoId);
      if (response.ok) {
        const filteredByStatus = (response.payload || []).filter(item => {
          const periodo = periodos.find(p => p.id === item.periodo_id);
          console.log('Filtrando periodo:', periodo, 'para item:', item);
          console.log('Estatus del periodo:', periodo ? periodo.estatus : 'No encontrado');
          return periodo && item.timbrado === "Timbrado";
        });

        const formattedData = filteredByStatus.map(item => ({
          ...item,
          periodo_tipo: getPeriodoText(item.periodo_id || 0),
          num_periodo: item.numero,
          timbrado: item.timbrado || 'No',
          key: item.id,
        }));
        setHistoricData(formattedData);
        message.success('Histórico cargado correctamente');
      } else {
        message.error('Error al obtener datos históricos');
        setHistoricData([]);
      }
    } catch (error) {
      console.error('Error al obtener histórico:', error);
      message.error('Error al cargar datos históricos');
      setHistoricData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEmpleadoChange = (value) => {
    setEmpleadoSeleccionado(value);
    const empleado = empleados.find(e => e.value === value);
    setEmpleadoInfo(empleado);
  };

  const columns = [
    {
      title: 'Periodo',
      dataIndex: 'periodo_tipo',
      key: 'periodo_tipo',
      width: 150,
      align: 'center',
    },
    {
      title: 'Año',
      dataIndex: 'anio',
      key: 'anio',
      width: 60,
      align: 'center',
    },
    {
      title: 'Num. Periodo',
      dataIndex: 'num_periodo',
      key: 'num_periodo',
      width: 100,
      align: 'center',
    },
    {
      title: 'Días',
      dataIndex: 'dias',
      key: 'dias',
      width: 60,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Horas Ord',
      dataIndex: 'horas',
      key: 'horas',
      width: 80,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Horas Extra',
      dataIndex: 'horas_extras',
      key: 'horas_extras',
      width: 90,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Deducción',
      dataIndex: 'deduccion',
      key: 'deduccion',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Neto',
      dataIndex: 'neto',
      key: 'neto',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Fecha Pago',
      dataIndex: 'fecha_pago',
      key: 'fecha_pago',
      width: 110,
      align: 'center',
    },
    {
      title: 'Timbrado',
      dataIndex: 'timbrado',
      key: 'timbrado',
      width: 150,
      align: 'center',
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 150,
    },
    {
      title: 'PAC',
      dataIndex: 'pac',
      key: 'pac',
      width: 150,
    },
    {
      title: 'Fecha Timbre',
      dataIndex: 'fecha_timbre',
      key: 'fecha_timbre',
      width: 110,
      align: 'center',
    },
  ];

  const filteredColumns = columns.filter(col => {
    if (historicData.length === 0) return true;
    
    // Siempre mostrar la columna de periodo
    if (['cancelar', 'periodo_tipo'].includes(col.key)) return true;
    
    return historicData.some(item => item[col.dataIndex] !== undefined);
  });

  return (
    <PageApp 
      routes={routes}
      title={'Histórico de Nóminas'}>
      
      <Card>
        <Row gutter={16}>
          <Col xs={24} sm={16} md={12} lg={10} xl={8}>
            <Form 
              form={form} 
              layout="horizontal"
              autoComplete="off">
              
              <Form.Item
                name="empleado_id"
                label="Empleado"
                rules={[{ required: true, message: 'Seleccione un empleado' }]}
              >
                <Select 
                  placeholder="Seleccione empleado" 
                  onChange={handleEmpleadoChange}
                  loading={loading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {empleados.map((item) => (
                    <Option key={item.value} value={item.value}>{item.text}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Col>
        </Row>
        
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Space>
              <Button 
                type="primary" 
                onClick={obtenerHistorico} 
                loading={loading}
                icon={<FileSearchOutlined />}
              >
                Buscar
              </Button>
              
              <Button 
                onClick={() => {
                  form.resetFields();
                  setHistoricData([]);
                  setEmpleadoInfo(null);
                }}
                icon={<CloseCircleOutlined />}
              >
                Limpiar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      
      {empleadoInfo && (
        <Card style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Space>
                  <Text strong>Empleado:</Text>
                  <Text>{empleadoInfo.text}</Text>
                </Space>
              </Col>
              
              {empleadoInfo.nomina_daily_salary && (
                <Col xs={24} sm={12}>
                  <Space>
                    <Text strong>Salario Diario:</Text>
                    <Currency value={empleadoInfo.nomina_daily_salary} />
                  </Space>
                </Col>
              )}
            </Row>
            
            <Row gutter={16}>
              {empleadoInfo.nomina_integrated_salary && (
                <Col xs={24} sm={12}>
                  <Space>
                    <Text strong>Salario Integrado:</Text>
                    <Currency value={empleadoInfo.nomina_integrated_salary} />
                  </Space>
                </Col>
              )}
              
              {empleadoInfo.nomina_employer_registration_key && (
                <Col xs={24} sm={12}>
                  <Space>
                    <Text strong>Registro Patronal:</Text>
                    <Text>{empleadoInfo.nomina_employer_registration_key}</Text>
                  </Space>
                </Col>
              )}
            </Row>
            
            {empleadoInfo.nomina_entry_date && (
              <Row>
                <Col xs={24}>
                  <Space>
                    <Text strong>Fecha de Ingreso:</Text>
                    <Text>{empleadoInfo.nomina_entry_date}</Text>
                  </Space>
                </Col>
              </Row>
            )}
          </Space>
        </Card>
      )}
      
      <Card style={{ marginTop: 16 }}>
        {loading ? (
          <Skeleton active={loading} />
        ) : (
          <Table 
            dataSource={historicData} 
            columns={filteredColumns} 
            pagination={{ pageSize: 15 }}
            size="small"
            bordered
            scroll={{ x: 'max-content' }}
            rowKey="id"
            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
            locale={{
              emptyText: <Empty description="No hay datos históricos disponibles" />
            }}
            style={{ 
              marginTop: 16,
              marginBottom: 16
            }}
          />
        )}
      </Card>
      
      <style jsx global>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #f5f5f5;
        }
        .ant-table-thead > tr > th {
          background-color: #f0f0f0;
          text-align: center;
          font-weight: bold;
        }
      `}</style>
    </PageApp>
  );
};

export default HistoricoNominaSimple;